import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import {
  clearToolsConnectorAction,
  enrichToolsConnectorAction,
  hydrateWorkflowDoc,
  patchAgentLiveNestedAction,
  patchAgentLiveRootAction,
  renameWorkflowScopeAction,
  resetForWorkflowAction,
  resetLayoutAction,
  saveAgentNestedAction,
  saveAgentRootAction,
  saveMainAction,
  saveNodeConfigAction,
  saveTriggerAction,
  saveWorkflowIdAction,
  type SerializedAgentTool,
  setAgentToolsAction,
  setAllAgentToolsAction} from './workflowBuilderActions';

export type AgentDrawerState =
  | { kind: 'root'; nodeId: string }
  | { kind: 'nested'; loopId: string; nodeId: string }
  | null;

export type WorkflowBuilderUiState = {
  docKey: number;
  /** Bumped only when the graph's identity or layout changes wholesale (load,
   *  template switch, reset layout) — the canvas re-fits on this, never on
   *  `docKey`, so an ordinary edit like renaming a block leaves zoom alone. */
  fitKey: number;
  triggerDrawerId: string | null;
  agentDrawer: AgentDrawerState;
  nodeDrawerId: string | null;
  pendingMainSaveByNodeId: Record<string, boolean>;
  mcpToolsByNodeId: Record<string, SerializedAgentTool[]>;
};

export const defaultBuilderUiState: WorkflowBuilderUiState = {
  docKey: 0,
  fitKey: 0,
  triggerDrawerId: null,
  agentDrawer: null,
  nodeDrawerId: null,
  pendingMainSaveByNodeId: {},
  mcpToolsByNodeId: {}
};

type WorkflowBuilderUiSliceState = {
  uiByWorkflowId: Record<string, WorkflowBuilderUiState>;
};

const initialState: WorkflowBuilderUiSliceState = {
  uiByWorkflowId: {}
};

function ensureUi(
  state: WorkflowBuilderUiSliceState,
  workflowId: string
): WorkflowBuilderUiState {
  if (!state.uiByWorkflowId[workflowId]) {
    state.uiByWorkflowId[workflowId] = { ...defaultBuilderUiState };
  }
  return state.uiByWorkflowId[workflowId];
}

const slice = createSlice({
  name: 'workflowBuilderUi',
  initialState,
  reducers: {
    openTriggerDrawer(
      state,
      action: PayloadAction<{ workflowId: string; nodeId: string }>
    ) {
      const ui = ensureUi(state, action.payload.workflowId);
      ui.triggerDrawerId = action.payload.nodeId;
      ui.agentDrawer = null;
      ui.nodeDrawerId = null;
    },
    closeTriggerDrawer(
      state,
      action: PayloadAction<{ workflowId: string }>
    ) {
      ensureUi(state, action.payload.workflowId).triggerDrawerId = null;
    },
    openRootAgentDrawer(
      state,
      action: PayloadAction<{ workflowId: string; nodeId: string }>
    ) {
      const ui = ensureUi(state, action.payload.workflowId);
      ui.agentDrawer = {
        kind: 'root',
        nodeId: action.payload.nodeId
      };
      ui.triggerDrawerId = null;
      ui.nodeDrawerId = null;
    },
    openNestedAgentDrawer(
      state,
      action: PayloadAction<{
        workflowId: string;
        loopId: string;
        nodeId: string;
      }>
    ) {
      const ui = ensureUi(state, action.payload.workflowId);
      ui.agentDrawer = {
        kind: 'nested',
        loopId: action.payload.loopId,
        nodeId: action.payload.nodeId
      };
      ui.triggerDrawerId = null;
      ui.nodeDrawerId = null;
    },
    closeAgentDrawer(state, action: PayloadAction<{ workflowId: string }>) {
      ensureUi(state, action.payload.workflowId).agentDrawer = null;
    },
    openNodeDrawer(
      state,
      action: PayloadAction<{ workflowId: string; nodeId: string }>
    ) {
      const ui = ensureUi(state, action.payload.workflowId);
      ui.nodeDrawerId = action.payload.nodeId;
      ui.triggerDrawerId = null;
      ui.agentDrawer = null;
    },
    closeNodeDrawer(state, action: PayloadAction<{ workflowId: string }>) {
      ensureUi(state, action.payload.workflowId).nodeDrawerId = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateWorkflowDoc, (state, action) => {
        const ui = ensureUi(state, action.payload.workflowId);
        ui.docKey += 1;
        ui.fitKey += 1;
        ui.triggerDrawerId = null;
        ui.agentDrawer = null;
        ui.nodeDrawerId = null;
        ui.pendingMainSaveByNodeId = {};
      })
      .addCase(saveWorkflowIdAction, (state, action) => {
        ensureUi(state, action.payload.workflowId).docKey += 1;
      })
      .addCase(saveTriggerAction, (state, action) => {
        const ui = ensureUi(state, action.payload.workflowId);
        ui.docKey += 1;
        ui.pendingMainSaveByNodeId[action.payload.triggerId] = true;
      })
      .addCase(saveAgentRootAction, (state, action) => {
        const ui = ensureUi(state, action.payload.workflowId);
        ui.docKey += 1;
        ui.pendingMainSaveByNodeId[action.payload.nodeId] = true;
        ui.agentDrawer = null;
      })
      .addCase(saveAgentNestedAction, (state, action) => {
        const ui = ensureUi(state, action.payload.workflowId);
        ui.docKey += 1;
        ui.pendingMainSaveByNodeId[action.payload.loopId] = true;
        ui.agentDrawer = null;
      })
      .addCase(patchAgentLiveRootAction, (state, action) => {
        ensureUi(state, action.payload.workflowId).docKey += 1;
      })
      .addCase(patchAgentLiveNestedAction, (state, action) => {
        ensureUi(state, action.payload.workflowId).docKey += 1;
      })
      .addCase(saveNodeConfigAction, (state, action) => {
        const ui = ensureUi(state, action.payload.workflowId);
        ui.docKey += 1;
        ui.pendingMainSaveByNodeId[action.payload.nodeId] = true;
      })
      .addCase(setAgentToolsAction, (state, action) => {
        const ui = ensureUi(state, action.payload.workflowId);
        ui.docKey += 1;
        ui.mcpToolsByNodeId[action.payload.nodeId] = action.payload.tools;
      })
      .addCase(setAllAgentToolsAction, (state, action) => {
        const ui = ensureUi(state, action.payload.workflowId);
        ui.docKey += 1;
        ui.mcpToolsByNodeId = action.payload.toolsByNodeId;
      })
      .addCase(enrichToolsConnectorAction, (state, action) => {
        const ui = ensureUi(state, action.payload.workflowId);
        const idSet = new Set(action.payload.actionIds);
        let changed = false;
        for (const nodeId of Object.keys(ui.mcpToolsByNodeId)) {
          const tools = ui.mcpToolsByNodeId[nodeId];
          const next = tools.map((tool) =>
            idSet.has(tool.rid) &&
            tool.connector_id !== action.payload.connectorId
              ? { ...tool, connector_id: action.payload.connectorId }
              : tool
          );
          if (next.some((t, i) => t !== tools[i])) {
            ui.mcpToolsByNodeId[nodeId] = next;
            changed = true;
          }
        }
        if (changed) {
          ui.docKey += 1;
        }
      })
      .addCase(clearToolsConnectorAction, (state, action) => {
        const ui = ensureUi(state, action.payload.workflowId);
        let changed = false;
        for (const nodeId of Object.keys(ui.mcpToolsByNodeId)) {
          const tools = ui.mcpToolsByNodeId[nodeId];
          const next = tools.map((tool) =>
            tool.category_code === action.payload.categoryCode &&
            tool.connector_id !== undefined
              ? { ...tool, connector_id: undefined }
              : tool
          );
          if (next.some((t, i) => t !== tools[i])) {
            ui.mcpToolsByNodeId[nodeId] = next;
            changed = true;
          }
        }
        if (changed) {
          ui.docKey += 1;
        }
      })
      .addCase(saveMainAction, (state, action) => {
        ensureUi(state, action.payload.workflowId).pendingMainSaveByNodeId =
          {};
      })
      .addCase(resetLayoutAction, (state, action) => {
        const ui = ensureUi(state, action.payload.workflowId);
        ui.docKey += 1;
        ui.fitKey += 1;
        ui.pendingMainSaveByNodeId = {};
      })
      .addCase(resetForWorkflowAction, (state, action) => {
        state.uiByWorkflowId[action.payload.workflowId] = {
          ...defaultBuilderUiState
        };
      })
      .addCase(renameWorkflowScopeAction, (state, action) => {
        const { from, to } = action.payload;
        const ui = state.uiByWorkflowId[from];
        if (from === to || !ui || state.uiByWorkflowId[to]) {
          return;
        }
        state.uiByWorkflowId[to] = ui;
        delete state.uiByWorkflowId[from];
      });
  }
});

export const reducer = slice.reducer;
export const {
  openTriggerDrawer,
  closeTriggerDrawer,
  openRootAgentDrawer,
  openNestedAgentDrawer,
  closeAgentDrawer,
  openNodeDrawer,
  closeNodeDrawer
} = slice.actions;
